import { useEffect, useRef, useState } from 'react'

interface UseElementVisibilityOptions {
  /**
   * Görünürlük eşiği (0-1 arası). Örneğin 0.1, elementin %10'u görünür olduğunda tetiklenir.
   * @default 0.01
   */
  threshold?: number

  /**
   * Görünürlük marjı. Elementin görünür olmasından ne kadar önce tetikleneceğini belirler.
   * @default '200px'
   */
  rootMargin?: string

  /**
   * Mobil cihazlar için farklı yapılandırma kullanılıp kullanılmayacağı
   * @default false
   */
  isMobileDevice?: boolean

  /**
   * Element görünür olduğunda observer'ı kaldırıp kaldırmayacağı
   * @default true
   */
  disconnectOnVisible?: boolean

  /**
   * DOM'un tamamen yüklenmesini beklemek için timeout süresi (ms)
   * @default 100
   */
  initDelay?: number
}

/**
 * Bir DOM elementinin görünürlüğünü takip eden hook
 * @param options Görünürlük takibi için seçenekler
 * @returns [ref, isVisible] - Element referansı ve görünürlük durumu
 */
export const useElementVisibility = <T extends HTMLElement = HTMLDivElement>(options: UseElementVisibilityOptions = {}) => {
  const { threshold = 0.01, rootMargin = '200px', isMobileDevice = false, disconnectOnVisible = true, initDelay = 100 } = options

  // Mobil cihazlar için daha agresif bir görünürlük kontrolü
  const effectiveRootMargin = isMobileDevice ? '50px' : rootMargin
  const effectiveThreshold = isMobileDevice ? Math.max(0.1, threshold) : threshold

  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef<T>(null)

  useEffect(() => {
    const currentElement = ref.current
    if (!currentElement) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true)

          // Element görünür olduktan sonra observer'ı kaldır
          if (disconnectOnVisible && currentElement) {
            observer.unobserve(currentElement)
          }
        } else if (!disconnectOnVisible) {
          // Eğer disconnectOnVisible false ise, element görünür olmadığında isVisible'ı false yap
          setIsVisible(false)
        }
      },
      {
        rootMargin: effectiveRootMargin,
        threshold: effectiveThreshold,
      }
    )

    // Timeout ekleyerek DOM'un tamamen yüklenmesini bekleyelim
    const timeoutId = setTimeout(() => {
      if (currentElement) {
        observer.observe(currentElement)
      }
    }, initDelay)

    return () => {
      clearTimeout(timeoutId)
      observer.disconnect()
    }
  }, [effectiveRootMargin, effectiveThreshold, disconnectOnVisible, initDelay])

  return [ref, isVisible] as const
}

export default useElementVisibility
